import { useMemo } from 'react'

import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { CompliantPaymentRelation } from '~/types'
import { EvmAddress } from '~/utils/address'
import { createSingletonBatcher } from '~/utils/requests'

type PayeeResult = Pick<
  CompliantPaymentRelation,
  | 'id'
  | 'createdAt'
  | 'address'
  | 'chains'
  | 'payeeOrg'
  | 'payeeInvitedUserId'
  | 'payeeInvitedEmail'
>

type ResultByAddress = Record<string, PayeeResult | undefined>
type PostFn = ReturnType<typeof useServerRequests>['serverPostRequest']

// Batches addresses for 100ms and makes one request to /api/org/compliance/payees-by-address
const getBatchPayeeLookup = (serverPostRequest: PostFn, orgId: string) => {
  return createSingletonBatcher<
    { address: string; orgId: string },
    string,
    PayeeResult | undefined,
    PayeeResult
  >(`payee-${orgId}`, {
    windowMs: 100,
    toKey: ({ address }) => EvmAddress.format(address),
    request: async (addresses) => {
      const result = await serverPostRequest<{ results: ResultByAddress }>({
        path: '/api/org/compliance/payees-by-address',
        params: {
          addresses,
          orgId,
        },
        // This is actually a fetch, it uses a POST to support large list of addresses,
        // so we don't need an error message
        errorMessage: '',
      })
      return result?.data?.results ?? {}
    },
    select: (payeeData) => payeeData,
  })
}

// Hook that provides a memoized batch lookup function
export const useBatchPayeeLookup = () => {
  const { serverPostRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()

  return useMemo(
    () =>
      activeOrgId ? getBatchPayeeLookup(serverPostRequest, activeOrgId) : null,
    [serverPostRequest, activeOrgId],
  )
}
